import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { DollarSign, Users, TrendingUp, ArrowRight, CheckCircle, BarChart3 } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';
import { validateFormField, isHoneypotFilled, HONEYPOT_FIELD, checkRateLimit, markFormLoaded, isSubmitTooFast, sanitizeText } from '../utils/formValidation';

const TIERS = [
  { name: 'Bronze', rate: '10%', refs: '0+', color: 'text-amber-600' },
  { name: 'Silver', rate: '15%', refs: '10+', color: 'text-gray-400' },
  { name: 'Gold', rate: '20%', refs: '25+', color: 'text-yellow-400' },
  { name: 'Platinum', rate: '25%', refs: '50+', color: 'text-cyan-300' },
];

const BENEFITS = [
  'Earn on every ghostwriting package sold',
  'Real-time tracking dashboard',
  'Automatic tier upgrades',
  'Monthly payouts via PayPal/Stripe',
  '90-day cookie duration',
  'Marketing materials provided',
];

export default function AffiliateLanding() {
  const [form, setForm] = useState({ name: '', email: '', password: '', [HONEYPOT_FIELD]: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => { markFormLoaded('affiliate'); }, []);

  const validate = () => {
    const e = {};
    const nameCheck = validateFormField(form.name, 'name');
    if (!nameCheck.valid) e.name = nameCheck.error;
    const emailCheck = validateFormField(form.email, 'email');
    if (!emailCheck.valid) e.email = emailCheck.error;
    if (form.password.length < 6) e.password = 'Min 6 characters.';
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (isHoneypotFilled(form[HONEYPOT_FIELD])) return;
    if (isSubmitTooFast('affiliate')) return;
    if (!checkRateLimit('affiliate', 5000)) { setError('Please wait a moment.'); return; }
    if (!validate()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/affiliate/quick-apply', {
        name: sanitizeText(form.name),
        email: sanitizeText(form.email),
        password: form.password
      });
      localStorage.setItem('affiliate_token', data.token);
      localStorage.setItem('affiliate', JSON.stringify(data.affiliate));
      window.location.href = '/affiliate/dashboard';
    } catch (e) {
      setError(e.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <>
      <SeoHead
        title="Partner Program — Earn Commissions Promoting Book Ghostwriting | AI Ghostwriter Pro"
        description="Join the AI Ghostwriter Pro partner program and earn up to 25% recurring commission on every referral. Promote premium ghostwriting services and earn passive income helping aspiring authors publish books."
        keywords="ghostwriting affiliate program, book writing affiliate, ghostwriter referral program, earn money promoting ghostwriting, ghostwriting partner program, passive income for writers"
        path="/affiliate"
      />

      <section className="pt-28 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          {/* Hero */}
          <div className="text-center mb-12 md:mb-20">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Partner Program</p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-4 md:mb-6">
              Earn Up To <span className="text-primary">25%</span> Commission
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">Promote AI Ghostwriter Pro and earn generous commissions on every sale. Join hundreds of affiliate partners earning passive income.</p>
          </div>

          {/* Commission Tiers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12 md:mb-20">
            {TIERS.map((t, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-6 text-center" data-testid={`tier-${t.name.toLowerCase()}`}>
                <p className={`font-serif text-3xl ${t.color}`}>{t.rate}</p>
                <p className="font-semibold text-foreground mt-2">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.refs} referrals</p>
              </div>
            ))}
          </div>

          {/* Benefits + Signup */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">
            <div>
              <h2 className="font-serif text-2xl md:text-3xl mb-8">Why Become an <span className="text-primary">Affiliate Partner?</span></h2>
              <ul className="space-y-4">
                {BENEFITS.map((b, i) => (
                  <li key={i} className="flex items-center gap-3 text-muted-foreground">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-8 bg-white/5 border border-white/10 rounded-sm p-6">
                <p className="text-sm text-muted-foreground">
                  <span className="text-primary font-semibold">Example:</span> Refer 5 Complete Package subscriptions ($599/mo each) at Bronze tier (10%) = <span className="text-primary font-semibold">$299.50/mo</span> in recurring commissions.
                </p>
              </div>
            </div>

            <div>
              <div className="bg-white/5 border border-white/10 rounded-sm p-8">
                <h3 className="font-serif text-xl mb-6">Join Now — It's Free</h3>
                {error && <p className="text-sm text-red-400 mb-4" data-testid="affiliate-signup-error">{error}</p>}
                <form onSubmit={handleSignup} className="space-y-4" data-testid="affiliate-signup-form" noValidate>
                  <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                    <input type="text" name={HONEYPOT_FIELD} value={form[HONEYPOT_FIELD]} onChange={(e) => setForm({...form, [HONEYPOT_FIELD]: e.target.value})} autoComplete="off" tabIndex={-1} />
                  </div>
                  <div>
                    <input type="text" required placeholder="Full name" value={form.name}
                      onChange={(e) => { setForm({...form, name: e.target.value}); if (fieldErrors.name) setFieldErrors({...fieldErrors, name: null}); }}
                      data-testid="affiliate-name" maxLength={100}
                      className={`w-full px-4 h-12 bg-white/5 border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none ${fieldErrors.name ? 'border-red-500/50' : 'border-white/10'}`} />
                    {fieldErrors.name && <p className="text-xs text-red-400 mt-1">{fieldErrors.name}</p>}
                  </div>
                  <div>
                    <input type="email" required placeholder="Email" value={form.email}
                      onChange={(e) => { setForm({...form, email: e.target.value}); if (fieldErrors.email) setFieldErrors({...fieldErrors, email: null}); }}
                      data-testid="affiliate-email" maxLength={254}
                      className={`w-full px-4 h-12 bg-white/5 border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none ${fieldErrors.email ? 'border-red-500/50' : 'border-white/10'}`} />
                    {fieldErrors.email && <p className="text-xs text-red-400 mt-1">{fieldErrors.email}</p>}
                  </div>
                  <div>
                    <input type="password" required minLength={6} placeholder="Password" value={form.password}
                      onChange={(e) => { setForm({...form, password: e.target.value}); if (fieldErrors.password) setFieldErrors({...fieldErrors, password: null}); }}
                      data-testid="affiliate-password"
                      className={`w-full px-4 h-12 bg-white/5 border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none ${fieldErrors.password ? 'border-red-500/50' : 'border-white/10'}`} />
                    {fieldErrors.password && <p className="text-xs text-red-400 mt-1">{fieldErrors.password}</p>}
                  </div>
                  <button type="submit" disabled={loading} data-testid="affiliate-signup-submit"
                    className="w-full rounded-sm py-3 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black">
                    {loading ? 'Joining...' : 'Become an Affiliate'}
                  </button>
                </form>
                <p className="text-center mt-4 text-xs text-muted-foreground">
                  Already a partner? <Link to="/affiliate/dashboard" className="text-primary hover:underline">Sign in</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
